import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';

const services = [
  {
    icon: '◈',
    title: 'IT Consulting',
    sanskrit: 'विवेक',
    sanskritMeaning: 'Discernment',
    description: 'Strategic guidance rooted in clarity. We approach your IT challenges with mindful planning, cutting through noise to find solutions aligned with your true objectives.',
    tags: ['Architecture Review', 'Tech Strategy', 'Digital Roadmap'],
  },
  {
    icon: '◎',
    title: 'Product Development',
    sanskrit: 'सृष्टि',
    sanskritMeaning: 'Creation',
    description: 'Software and product solutions that balance innovation with stillness. We build with intention — every feature earns its place, every line of code serves purpose.',
    tags: ['Web Applications', 'Mobile', 'SaaS Platforms'],
  },
  {
    icon: '⟡',
    title: 'AI & Automation',
    sanskrit: 'यन्त्र',
    sanskritMeaning: 'Mechanism',
    description: 'Intelligent workflow systems designed with deep intentionality. We harness AI not as a trend, but as a tool — crafted to amplify human potential mindfully.',
    tags: ['Workflow Automation', 'ML Integration', 'Process Intelligence'],
  },
  {
    icon: '◇',
    title: 'Digital Marketing',
    sanskrit: 'प्रसार',
    sanskritMeaning: 'Outreach',
    description: 'Data-driven brand strategies that resonate authentically. We align your digital presence with your core truth — attracting the right audience through purposeful messaging.',
    tags: ['SEO & Content', 'Brand Strategy', 'Analytics'],
  },
  {
    icon: '⬡',
    title: 'Business Transformation',
    sanskrit: 'परिवर्तन',
    sanskritMeaning: 'Transformation',
    description: 'Modern operations optimization that honors your organization\'s rhythm. We guide transitions that are sustainable, humane, and aligned with long-term vision.',
    tags: ['Process Redesign', 'Change Management', 'Org Efficiency'],
  },
  {
    icon: '✦',
    title: 'Tech Festivals',
    sanskrit: 'उत्सव',
    sanskritMeaning: 'Celebration',
    description: 'Conferences and gatherings in serene, nature-integrated settings. Where industry professionals reconnect with purpose, exchange insights, and rediscover inspiration.',
    tags: ['Conferences', 'Retreats', 'Knowledge Events'],
  },
];

const ServiceCard = ({ service, index, inView }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered
          ? 'linear-gradient(135deg, rgba(232,115,42,0.06), rgba(201,168,76,0.04))'
          : 'rgba(255,255,255,0.02)',
        border: `1px solid ${hovered ? 'rgba(201,168,76,0.35)' : 'rgba(201,168,76,0.1)'}`,
        borderRadius: 12,
        padding: '36px 32px',
        cursor: 'default',
        transition: 'all 0.4s ease',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Glow on hover */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'absolute',
              top: 0, right: 0,
              width: 100, height: 100,
              background: 'radial-gradient(circle, rgba(232,115,42,0.15), transparent)',
              pointerEvents: 'none',
            }}
          />
        )}
      </AnimatePresence>

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div style={{
          fontSize: 28,
          color: hovered ? '#E8732A' : '#C9A84C',
          transition: 'color 0.3s',
          lineHeight: 1,
        }}>
          {service.icon}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'Cinzel', fontSize: 18, color: 'rgba(201,168,76,0.3)', lineHeight: 1 }}>
            {service.sanskrit}
          </div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: '#6B6560', textTransform: 'uppercase' }}>
            {service.sanskritMeaning}
          </div>
        </div>
      </div>

      <h3 style={{
        fontFamily: 'Cinzel',
        fontSize: 18,
        color: '#F5F0E8',
        letterSpacing: 1,
        marginBottom: 14,
      }}>
        {service.title}
      </h3>

      <p style={{ color: '#B0A898', lineHeight: 1.8, fontSize: 14, marginBottom: 24 }}>
        {service.description}
      </p>

      {/* Tags */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {service.tags.map(tag => (
          <span
            key={tag}
            style={{
              background: 'rgba(201,168,76,0.06)',
              border: '1px solid rgba(201,168,76,0.15)',
              borderRadius: 20,
              padding: '4px 12px',
              fontSize: 11,
              color: '#B0A898',
              letterSpacing: 0.5,
            }}
          >
            {tag}
          </span>
        ))}
      </div>
    </motion.div>
  );
};

export default function Services() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="services" ref={ref} style={{
      padding: '120px 5%',
      background: '#0D0D0D',
      position: 'relative',
    }}>
      {/* Background mandala */}
      <div style={{
        position: 'absolute',
        right: -200,
        top: '20%',
        width: 600,
        height: 600,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(232,115,42,0.03) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          style={{ textAlign: 'center', marginBottom: 80 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 20 }}>
            <div style={{ width: 60, height: 1, background: 'linear-gradient(to right, transparent, #C9A84C)' }} />
            <span style={{ fontFamily: 'Cinzel', fontSize: 11, letterSpacing: 4, color: '#C9A84C', textTransform: 'uppercase' }}>What We Do</span>
            <div style={{ width: 60, height: 1, background: 'linear-gradient(to left, transparent, #C9A84C)' }} />
          </div>
          <h2 style={{
            fontFamily: 'Cinzel',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            color: '#F5F0E8',
            letterSpacing: 2,
            marginBottom: 20,
          }}>
            Our Services
          </h2>
          <p style={{ maxWidth: 560, margin: '0 auto', color: '#B0A898', lineHeight: 1.8 }}>
            Six pillars of purposeful technology — each one crafted with the mindfulness of Vedic tradition and the precision of modern engineering.
          </p>
        </motion.div>

        {/* Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 24,
        }}>
          {services.map((service, i) => (
            <ServiceCard key={service.title} service={service} index={i} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
}
